import { useState } from 'react';
import { useDebounce } from 'react-use';

import { useAtom } from 'jotai';

import { useSocialInboxDataSource } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxDataSource';
import { useSocialInboxNavigation } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxNavigation';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils';

import type {
    DMConversation,
    MessageFilter
} from '@/components/pages/conversations/SocialInbox/types';

// Constants/Libs/Types
import {
    DM_FILTER_OPTIONS,
    SORT_OPTIONS
} from '@/components/pages/conversations/SocialInbox/const';

// Atoms
import {
    messageFilterSecondaryAtom,
    selectedMessagesAtom
} from '@/components/pages/conversations/SocialInbox/atoms';

import SelectAllCheckbox from '@/components/base/Checkbox/SelectAllCheckbox';
// Components
import Dropdown from '@/components/base/Dropdown';
import Icon from '@/components/base/Icon';
import { DirectMessageActions } from '@/components/pages/conversations/SocialInbox/components/SocialInboxMessageList/SocialInboxHeader/SocialInboxActions/DirectMessageActions/DirectMessageActions';

export const DirectMessageFilters = () => {
    const { t } = useTranslation('conversations');
    const [selectedMessages, setSelectedMessages] = useAtom(selectedMessagesAtom);
    const { currentSocialInbox } = useSocialInboxNavigation();
    const { messages } = useSocialInboxDataSource() as { messages: DMConversation[] };

    const [filterState, setFilterState] = useAtom(messageFilterSecondaryAtom);

    const [inputValue, setInputValue] = useState('');

    const isAllSelected =
        messages.length > 0 &&
        messages.every((message) =>
            selectedMessages[currentSocialInbox].includes(message.meta_conversation_id.toString())
        );
    const isPartiallySelected = !isAllSelected && selectedMessages[currentSocialInbox].length > 0;

    // Debounce search input
    useDebounce(() => setFilterState((prev) => ({ ...prev, search: inputValue })), 200, [
        inputValue
    ]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleSort = (value: MessageFilter['sort']) => {
        setFilterState((prev) => ({ ...prev, sort: value }));
    };

    const handleSelectAll = () => {
        if (isAllSelected || selectedMessages[currentSocialInbox].length > 0) {
            // Deselect all messages
            setSelectedMessages({
                ...selectedMessages,
                [currentSocialInbox]: []
            });
        } else {
            // Select all messages
            setSelectedMessages({
                ...selectedMessages,
                [currentSocialInbox]: messages.map((message) =>
                    message.meta_conversation_id.toString()
                )
            });
        }
    };

    return (
        <div className="flex w-full flex-col gap-2">
            <div className="flex flex-wrap justify-between gap-4">
                <div className="border-icoGray-200 flex h-fit min-w-[25%] flex-1 flex-row items-center gap-2 overflow-hidden rounded-md border p-xxs text-icoBlack dark:border-icoDarkMode-beton dark:text-white">
                    <Icon
                        icon="search"
                        className="text-xs text-icoGray-400 dark:text-icoDarkMode-wolf"
                    />
                    <input
                        type="text"
                        placeholder={t('direct_messages.search_placeholder')}
                        value={inputValue}
                        className="h-3 flex-1 border-none bg-transparent p-0 font-GSemiBold text-xs placeholder:text-icoGray-400 focus:outline-none focus:ring-0 dark:placeholder:text-icoDarkMode-wolf"
                        onChange={handleSearch}
                    />
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2">
                    <Dropdown
                        className="z-1 w-fit"
                        button={
                            <Dropdown.Button className="flex items-center text-icoBlack dark:text-white">
                                <div className="flex items-center gap-1">
                                    <span className="font-GSemiBold text-xs text-icoBlack dark:text-white">
                                        {t('direct_messages.sort_by')}
                                    </span>
                                    <Icon
                                        icon="chevron-bottom"
                                        className="text-xs text-icoGray-400 dark:text-icoDarkMode-wolf"
                                    />
                                </div>
                            </Dropdown.Button>
                        }>
                        {SORT_OPTIONS.map((option) => (
                            <Dropdown.Item
                                key={option.value}
                                onClick={() => handleSort(option.value)}>
                                {({ active }) => (
                                    <div
                                        className={classNames(
                                            'rounded-sm px-4 py-2 font-GSemiBold text-xs',
                                            active
                                                ? 'bg-icoBlue-100 text-icoBlue dark:bg-icoBlue/20'
                                                : 'text-icoBlack hover:bg-icoBlue-100 hover:text-icoBlue dark:text-white dark:hover:bg-icoBlue/20'
                                        )}>
                                        {t(`direct_messages.sort_options.${option.value}`)}
                                    </div>
                                )}
                            </Dropdown.Item>
                        ))}
                    </Dropdown>

                    <div className="h-full w-px bg-icoGray-300 dark:bg-icoDarkMode-beton" />

                    {!!selectedMessages[currentSocialInbox].length && (
                        <>
                            <DirectMessageActions />

                            <div className="h-full w-px bg-icoGray-300 dark:bg-icoDarkMode-beton" />
                        </>
                    )}

                    <SelectAllCheckbox
                        boxSize="sm"
                        checked={isAllSelected}
                        someSelected={isPartiallySelected}
                        onChange={handleSelectAll}
                        label={
                            <span className="hidden font-GSemiBold text-xs sm:block">
                                {t('social_inbox.select_all')}
                            </span>
                        }
                        containerClassName="text-icoBlack dark:text-white"
                    />
                </div>
            </div>

            <div className="flex flex-1 items-center gap-2">
                {DM_FILTER_OPTIONS.map((option) => (
                    <button
                        key={option.value}
                        onClick={() =>
                            setFilterState((prev) => ({ ...prev, filter: option.value }))
                        }
                        className={classNames(
                            'flex-1 rounded-md p-3 font-GSemiBold text-xs transition-colors',
                            filterState.filter === option.value
                                ? 'bg-icoBlue-100 text-icoBlue dark:bg-icoBlue/20'
                                : 'bg-icoGray-100 text-icoBlack hover:bg-icoBlue-100 hover:text-icoBlue dark:bg-icoDarkMode-anthracite dark:text-icoDarkMode-wolf dark:hover:bg-icoBlue/20 dark:hover:text-icoBlue'
                        )}>
                        {t(`direct_messages.filters.${option.value}`)}
                    </button>
                ))}
            </div>
        </div>
    );
};
